import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Users, ShieldCheck, MessageSquare, AlertTriangle, Heart, Sparkles, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [s, u, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users", { params: q ? { q } : {} }),
        api.get("/admin/reports"),
      ]);
      setStats(s.data); setUsers(u.data.users); setReports(r.data.reports);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [q]);

  const verify = async (uid, v) => {
    try { await api.post("/admin/verify", { user_id: uid, verified: v }); toast.success(`User ${v ? "verified" : "unverified"}`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const resolve = async (rid, status) => {
    try { await api.post("/admin/reports/resolve", { report_id: rid, status }); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-cream-100">
      <div className="flex">
        <aside className="w-64 min-h-screen bg-navy text-cream-100 p-6 hidden md:block">
          <div className="flex items-center gap-2">
            <Logo size={22} />
          </div>
          <div className="text-xs uppercase tracking-widest text-cream-100/50 mt-6 mb-3">Admin</div>
          <nav className="space-y-1 text-sm">
            {[
              ["overview", "Overview", Sparkles],
              ["users", "Users", Users],
              ["reports", "Reports", AlertTriangle],
            ].map(([k, l, Ic]) => (
              <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg ${tab === k ? "bg-cream-100/10 text-cream-100" : "text-cream-100/70 hover:bg-cream-100/5"}`}>
                <Ic size={16} /> {l}
              </button>
            ))}
          </nav>
          <div className="absolute bottom-6 space-y-2">
            <Link to="/app" className="text-xs text-cream-100/70 hover:underline block">← Back to app</Link>
            <button data-testid="admin-logout" onClick={logout} className="text-xs text-cream-100/70 hover:underline flex items-center gap-1"><LogOut size={12} /> Sign out</button>
          </div>
        </aside>

        <main className="flex-1 p-6 md:p-10">
          <div className="flex items-center justify-between">
            <h1 className="font-display text-4xl text-navy">Wholesome Admin</h1>
            <div className="flex md:hidden gap-2">
              {["overview", "users", "reports"].map((k) => (
                <button key={k} onClick={() => setTab(k)} className={`text-xs px-3 py-1.5 rounded-full ${tab === k ? "bg-navy text-cream-100" : "bg-white/60 text-navy"}`}>{k}</button>
              ))}
            </div>
          </div>

          {tab === "overview" && (
            <div className="mt-8 grid md:grid-cols-4 gap-4">
              {[
                ["Total users", stats.users, Users],
                ["Verified", stats.verified, ShieldCheck],
                ["Premium", stats.premium, Sparkles],
                ["Conversations", stats.conversations, MessageSquare],
                ["Messages", stats.messages, MessageSquare],
                ["Open reports", stats.open_reports, AlertTriangle],
                ["Gratitude entries", stats.gratitude_entries, Heart],
              ].map(([l, v, Ic], i) => (
                <div key={i} className="glass rounded-3xl p-6" data-testid={`stat-${l.toLowerCase().replace(/\s/g, "-")}`}>
                  <Ic size={18} className="text-rose" />
                  <div className="text-xs uppercase tracking-widest text-navy/60 mt-2">{l}</div>
                  <div className="font-display text-3xl text-navy mt-1">{v ?? 0}</div>
                </div>
              ))}
            </div>
          )}

          {tab === "users" && (
            <div className="mt-8">
              <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search users by name or email…" className="w-full max-w-md px-4 py-3 rounded-2xl bg-white border border-navy/10" />
              <div className="mt-4 glass rounded-3xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="text-left text-navy/60 bg-cream-200">
                    <tr>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Age</th>
                      <th className="px-4 py-3">City</th>
                      <th className="px-4 py-3">Verified</th>
                      <th className="px-4 py-3">Premium</th>
                      <th className="px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-t border-navy/5" data-testid={`user-row-${u.id}`}>
                        <td className="px-4 py-3">{u.name}</td>
                        <td className="px-4 py-3 text-navy/70">{u.email}</td>
                        <td className="px-4 py-3">{u.age}</td>
                        <td className="px-4 py-3">{u.city || "—"}</td>
                        <td className="px-4 py-3">{u.verified ? "✓" : "—"}</td>
                        <td className="px-4 py-3">{u.premium ? "✓" : "—"}</td>
                        <td className="px-4 py-3">
                          <button data-testid={`verify-${u.id}`} onClick={() => verify(u.id, !u.verified)}
                            className="text-xs px-3 py-1.5 rounded-full bg-navy text-cream-100 hover:bg-navy-600">
                            {u.verified ? "Unverify" : "Verify"}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-navy/50">No users.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "reports" && (
            <div className="mt-8 space-y-3">
              {reports.map((r) => (
                <div key={r.id} className="glass rounded-2xl p-5 flex items-center justify-between" data-testid={`report-${r.id}`}>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-rose">{r.reason}</div>
                    <div className="text-navy mt-1">Target: {r.target_user_id}</div>
                    {r.detail && <div className="text-sm text-navy/70 mt-1">{r.detail}</div>}
                    <div className="text-[10px] text-navy/50 mt-1">Status: {r.status} · {new Date(r.at).toLocaleString()}</div>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`report-resolve-${r.id}`} onClick={() => resolve(r.id, "resolved")} className="text-xs px-3 py-1.5 rounded-full bg-sage text-white">Resolve</button>
                    <button data-testid={`report-dismiss-${r.id}`} onClick={() => resolve(r.id, "dismissed")} className="text-xs px-3 py-1.5 rounded-full bg-white/70 border border-navy/10 text-navy">Dismiss</button>
                  </div>
                </div>
              ))}
              {reports.length === 0 && <div className="glass rounded-3xl p-8 text-center text-navy/50">No reports.</div>}
            </div>
          )}

          {err && <div className="text-sm text-rose-600 mt-4">{err}</div>}
        </main>
      </div>
    </div>
  );
}
